/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { Text } from '@fluentui/react/lib/Text';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { Container } from 'src/core/components/Container';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist } from 'src/core/themes/luisStylist';
import { AccuracySection } from 'src/luis/projects/analytics/components/IntentsWithErrorsCard/FaultyIntentDetails/AccuracySection';
import { DataImbalanceProblem } from 'src/luis/projects/analytics/components/IntentsWithErrorsCard/FaultyIntentDetails/DataImbalanceProblem';
import { FaultyIntent } from 'src/luis/projects/analytics/types/FaultyIntent';
import { IntentMetadata } from 'src/luis/projects/analytics/types/IntentMetadata';
import { PassedFilters } from 'src/luis/projects/analytics/types/PassedFilters';
import { LuisId } from 'src/luis/types/Resource';

export type FaultyIntentDetailsProps = {
    /**
     * The selected intent to discuss its problems.
     */
    selectedIntent: IntentMetadata;

    /**
     * Flag to indicate id the selected intent is needy.
     */
    isNeedyIntent?: boolean;

    /**
     * Flag to indicate id the selected intent is greedy.
     */
    isGreedyIntent?: boolean;

    /**
     * The intent that has the larger number of utterances in the app.
     */
    mostGreedyIntent: IntentMetadata;

    /**
     * The intent that has the least number of utterances.
     */
    mostNeedyIntent: IntentMetadata;

    /**
     * The mean of the number of utterances in the app.
     */
    meanNumberOfUtterances: number;

    /**
     * Array of the intents that conflict with the current intent
     * where the conflict is misclassification. e.g the utterances
     * that belong to the current intent and predicted as one of
     * this array intents.
     */
    incorrectlyConflictedIntents: FaultyIntent[];

    /**
     * Array of the intents that conflict with the current intent
     * where the conflict is ambiguity. e.g the utterances that
     * belong to the current intent but they are also highly predicted
     * to another intents with small difference between the the
     * prediction of the ambiguous intent and the labeled intent.
     */
    unclearConflictedIntents: FaultyIntent[];

    /**
     * A callback for when an intent is clicked.
     */
    onIntentClick?: (intentId: LuisId, filters: PassedFilters) => void;
};

const messages = defineMessages({
    detailedIntentSubtitle: {
        id: 'analytics.intentsWithErrorsCard.detailedIntentSubtitle',
        defaultMessage: 'correctly predicted'
    },
    noSelectedIntentsMsg: {
        id: 'analytics.intentsWithErrorsCard.noSelectedIntentsMsg',
        defaultMessage: 'No intent selected.'
    }
});

const { getClassName } = getStylist('Dashboard');

const emptyMsg = getClassName('emptyMsg', theme => ({
    color: theme.statsComparisonColor
}));

const emptyMsgWrapper = getClassName('emptyMsgWrapper', {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    justifyContent: 'center',
    width: '100%',
    height: '100%'
});

/**
 * The details of the selected intent either it has any one of the three problems
 * data imbalance, unclear predictions and incorrect predictions.
 *
 * @param props The properties of faulty intent.
 */
export const FaultyIntentDetails = (props: FaultyIntentDetailsProps) => {
    const { selectedIntent } = props;
    const { localize } = useLocalization();

    return (
        <Container size="side" tokens={{ childrenGap: 25 }} overflow={false}>
            {selectedIntent ? (
                <SelectedIntentDetails {...props} />
            ) : (
                <div className={emptyMsgWrapper}>
                    <Text className={emptyMsg}>{localize(messages.noSelectedIntentsMsg)}</Text>
                </div>
            )}
        </Container>
    );
};

/**
 * The content of the details of the selected intent.
 *
 * @param props The properties of the content.
 */
const SelectedIntentDetails = (props: FaultyIntentDetailsProps) => {
    const {
        selectedIntent,
        isNeedyIntent,
        isGreedyIntent,
        mostGreedyIntent,
        meanNumberOfUtterances,
        mostNeedyIntent,
        incorrectlyConflictedIntents,
        unclearConflictedIntents,
        onIntentClick
    } = props;
    const { localize } = useLocalization();

    return (
        <>
            <Stack horizontal tokens={{ childrenGap: 20 }} verticalAlign="baseline">
                <EditableTitle title={selectedIntent.intentName} />

                <LuisText>
                    {selectedIntent.correctAccuracy.toFixed(1)}% {localize(messages.detailedIntentSubtitle)}
                </LuisText>
            </Stack>

            {(isNeedyIntent || isGreedyIntent) && (
                <DataImbalanceProblem
                    isNeedyIntent={isNeedyIntent}
                    mean={meanNumberOfUtterances}
                    mostNeedyIntent={mostNeedyIntent}
                    selectedIntent={selectedIntent}
                    mostGreedyIntent={mostGreedyIntent}
                    onIntentClick={id => onIntentClick(id, null)}
                />
            )}

            <AccuracySection
                selectedIntent={selectedIntent}
                incorrectlyConflictedIntents={incorrectlyConflictedIntents}
                unclearConflictedIntents={unclearConflictedIntents}
                onIntentClick={onIntentClick}
            />
        </>
    );
};
